import React, { useEffect, useRef, useState } from "react"
import ChatContactItem from "../models/ChatContactItem"
import Icon from "../../../components/base/Icon"
import { FontAwesomeIcons } from "../../../core/Types"
import { ChatEnvironments, MediaTypes } from "../../../core/Enums"
import { addOrUpdateChat, hasRole, isDemo, openSettingsChat, removeChat, selectChat, toShortDate, useHub, useSelectedChat } from "../../../core/Global"
import { API_ROUTES } from "../../../core/Constants"
import ChatAvatarSpan from "./ChatAvatarSpan"

interface IProps {
    item?: ChatContactItem
    seperator?: boolean
    readed?: boolean
}

interface SwipeAction {
    key: string
    icon: FontAwesomeIcons
    label: string
    color: string
    run: () => void
}

const BTN_W = 74

// only one row stays open at a time
let closeOpenSwipe: (() => void) | null = null

const ChatMobileRow = (props: IProps) => {

    const hub = useHub()
    const [unreads, unreadsSet] = useState<number>(0)
    const selectedChat = useSelectedChat()

    const item = props.item

    const rowRef = useRef<HTMLDivElement>(null)
    const offsetRef = useRef<number>(0)
    const openRef = useRef<boolean>(false)
    const startX = useRef<number>(0)
    const startY = useRef<number>(0)
    const mode = useRef<"idle" | "h" | "v">("idle")
    const draggedRef = useRef<boolean>(false)

    useEffect(() => { unreadsSet(item?.unreadCount ?? 0) }, [item])
    useEffect(() => { if (props.readed) unreadsSet(0) }, [props.readed])

    // ---- build swipe actions ----
    // The Office and Rinvox chats are pinned and archived like any other; only their settings
    // (title, forwarding) stay out of reach.
    const actions: SwipeAction[] = []
    if (item) {
        if (item.archived) {
            if (hasRole("ArchiveChat"))
                actions.push({
                    key: "unarchive", icon: "arrow-up-from-bracket", label: "Unarchive", color: "#007aff",
                    // The response carries the unarchived chat; putting it in the list right away
                    // makes the row leave the archived view without waiting for the live-hub push
                    // (which arrives as well and is idempotent).
                    run: () => hub.Put(API_ROUTES.Chat + "unarchive/" + item.id, {}, res => {
                        if (res.success) addOrUpdateChat(res.data ?? { ...item, archived: false })
                    })
                })
        } else {
            actions.push({
                key: "pin", icon: "thumbtack", label: item.pinned ? "Unpin" : "Pin", color: "#ff9500",
                run: () => hub.Get(API_ROUTES.Chat + (item.pinned ? "unpin/" : "pin/") + item.chatId, res => {
                    if (!res.success && res.message == "pinnedexceed") window.openModal("modal-pinned-chats-exceeed")
                })
            })
            if (hasRole("ArchiveChat"))
                actions.push({
                    key: "archive", icon: "box-archive", label: "Archive", color: "#5856d6",
                    run: () => hub.Put(API_ROUTES.Chat + "archive/" + item.id, {}, res => {
                        if (res.success) removeChat(item.chatId)
                    })
                })
        }
        if (!item.isSystem)
            actions.push({
                key: "settings", icon: "gear", label: "Settings", color: "#8e8e93",
                run: () => { if (item) openSettingsChat(item) }
            })
    }
    const width = actions.length * BTN_W
    const swipeable = width > 0

    // ---- swipe mechanics (direct DOM for smoothness) ----
    const applyOffset = (x: number, animate: boolean) => {
        const el = rowRef.current
        if (!el) return
        el.style.transition = animate ? "transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)" : "none"
        el.style.transform = `translateX(${x}px)`
        offsetRef.current = x
    }

    const closeSelf = useRef<() => void>()
    if (!closeSelf.current) {
        closeSelf.current = () => {
            applyOffset(0, true)
            openRef.current = false
            if (closeOpenSwipe === closeSelf.current) closeOpenSwipe = null
        }
    }

    const openSelf = () => {
        applyOffset(-width, true)
        openRef.current = true
        if (closeOpenSwipe && closeOpenSwipe !== closeSelf.current) closeOpenSwipe()
        closeOpenSwipe = closeSelf.current!
    }

    useEffect(() => () => { if (closeOpenSwipe === closeSelf.current) closeOpenSwipe = null }, [])

    const onPointerDown = (e: React.PointerEvent) => {
        if (!swipeable || e.pointerType === "mouse") return
        startX.current = e.clientX
        startY.current = e.clientY
        mode.current = "idle"
        draggedRef.current = false
    }

    const onPointerMove = (e: React.PointerEvent) => {
        if (!swipeable) return
        const dx = e.clientX - startX.current
        const dy = e.clientY - startY.current

        if (mode.current === "idle") {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return
            mode.current = Math.abs(dx) > Math.abs(dy) ? "h" : "v"
            if (mode.current === "h") rowRef.current?.setPointerCapture(e.pointerId)
        }
        if (mode.current !== "h") return

        draggedRef.current = true
        const base = openRef.current ? -width : 0
        let x = base + dx
        if (x > 0) x = 0
        if (x < -width) x = -width - (x + width) * 0.2   // slight resistance past the edge
        applyOffset(x, false)
    }

    const onPointerUp = (e: React.PointerEvent) => {
        if (mode.current !== "h") { mode.current = "idle"; return }
        mode.current = "idle"
        if (offsetRef.current < -width / 2) openSelf()
        else closeSelf.current!()
    }

    const onRowClick = () => {
        if (draggedRef.current) { draggedRef.current = false; return }
        if (openRef.current) { closeSelf.current!(); return }
        if (item) selectChat(item)
    }

    return (
        <div className="chat-mobile-swipe">

            {swipeable &&
                <div className="chat-mobile-swipe-actions" style={{ width }}>
                    {actions.map(a =>
                        <div key={a.key} className="chat-swipe-btn"
                            style={{ width: BTN_W, backgroundColor: a.color }}
                            onClick={ev => {
                                ev.stopPropagation()
                                a.run()
                                closeSelf.current!()
                            }}>
                            <Icon icon={a.icon} type="solid" size={17} />
                            <span>{a.label}</span>
                        </div>
                    )}
                </div>}

            <div ref={rowRef}
                className={"chat-mobile-row" + (selectedChat?.id == item?.id ? " chat-mobile-row-selected" : "")}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onClick={onRowClick}>

                <div className="symbol symbol-50px symbol-circle chat-mobile-avatar">
                    {
                        item?.avatar
                            ? <img alt={item.title} src={item.avatar} />
                            : <ChatAvatarSpan item={item} size={20} />
                    }
                </div>

                <div className="chat-mobile-main">

                    <div className="chat-mobile-line1">
                        <span className={"chat-mobile-name" + (unreads > 0 ? " chat-mobile-name-unread" : "")}>
                            {isDemo() ? "Tech " + item?.title?.substring(0, 2).toUpperCase() : item?.title}
                        </span>
                        {item?.isSystem &&
                            <Icon icon="badge-check" type="solid" className="text-primary chat-mobile-badge" title="Verified Rinvox chat" />}
                        {item?.environment == ChatEnvironments.App &&
                            <Icon icon="mobile-alt" className="text-primary chat-mobile-badge" />}
                        {item?.environment == ChatEnvironments.WhatsApp &&
                            <Icon icon="whatsapp" type="brands" className="text-success chat-mobile-badge" />}
                        {item?.environment == ChatEnvironments.Telegram &&
                            <Icon icon="telegram" type="brands" className="text-primary chat-mobile-badge" />}
                        <span className="chat-mobile-time">
                            {item?.pinned && <Icon icon="thumbtack" type="solid" size={10} className="me-1 chat-mobile-pin" />}
                            {toShortDate(item?.lastUpdateDate)}
                        </span>
                    </div>

                    <div className="chat-mobile-line2">
                        <div className="chat-mobile-preview">
                            {(item?.hasMedia && item.mediaType) &&
                                <span className="me-1">
                                    {item.mediaType == MediaTypes.Audio ? <Icon icon="volume" />
                                        : item.mediaType == MediaTypes.Image ? <Icon icon="image" />
                                            : item.mediaType == MediaTypes.Video ? <Icon icon="video" />
                                                : <Icon icon="paperclip-vertical" />}
                                </span>}
                            <span>{item?.text}</span>
                        </div>

                        {unreads > 0 &&
                            <span className="chat-mobile-unread">{unreads}</span>}
                    </div>

                </div>

                {props.seperator && <div className="chat-mobile-separator" />}
            </div>
        </div>
    )
}

export default React.memo(ChatMobileRow)
